/**
 * Image utility functions for resizing, compressing, and processing images
 * before upload to cloud storage.
 */

export interface ImageDimensions {
  maxWidth: number;
  maxHeight: number;
}

export interface ProcessedImage {
  blob: Blob;
  dataUrl: string;
  width: number;
  height: number;
}

// Target dimensions for different image uses
export const IMAGE_SIZES = {
  collectionCover: { maxWidth: 1200, maxHeight: 900 },
  itemThumbnail: { maxWidth: 400, maxHeight: 400 },
  itemFull: { maxWidth: 1600, maxHeight: 1600 },
  customField: { maxWidth: 800, maxHeight: 800 },
  feedback: { maxWidth: 1200, maxHeight: 1200 },
} as const;

// Quality settings
export const IMAGE_QUALITY = {
  high: 0.9,
  standard: 0.85,
  thumbnail: 0.8,
} as const;

/**
 * Load an image from a file
 */
export function loadImageFromFile(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Load an image from a data URL
 */
export function loadImageFromDataUrl(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = dataUrl;
  });
}

/**
 * Calculate new dimensions while maintaining aspect ratio
 */
export function calculateDimensions(
  originalWidth: number,
  originalHeight: number,
  maxWidth: number,
  maxHeight: number
): { width: number; height: number } {
  // If image is smaller than max dimensions, keep original size
  if (originalWidth <= maxWidth && originalHeight <= maxHeight) {
    return { width: originalWidth, height: originalHeight };
  }

  const aspectRatio = originalWidth / originalHeight;
  
  let newWidth = maxWidth;
  let newHeight = maxWidth / aspectRatio;
  
  if (newHeight > maxHeight) {
    newHeight = maxHeight;
    newWidth = maxHeight * aspectRatio;
  }
  
  return {
    width: Math.round(newWidth),
    height: Math.round(newHeight),
  };
}

/**
 * Resize and compress an image
 */
export async function processImage(
  file: File,
  dimensions: ImageDimensions,
  quality: number = IMAGE_QUALITY.standard
): Promise<ProcessedImage> {
  const img = await loadImageFromFile(file);
  
  const { width, height } = calculateDimensions(
    img.naturalWidth,
    img.naturalHeight,
    dimensions.maxWidth,
    dimensions.maxHeight
  );
  
  // Create canvas for resizing
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Could not get canvas context');
  }
  
  // Use high-quality image rendering
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  
  // Draw resized image
  ctx.drawImage(img, 0, 0, width, height);
  
  // Clean up object URL
  URL.revokeObjectURL(img.src);
  
  // Convert to blob
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Failed to create image blob'));
          return;
        }
        
        // Also create data URL for preview
        const dataUrl = canvas.toDataURL('image/jpeg', quality);
        
        resolve({
          blob,
          dataUrl,
          width,
          height,
        });
      },
      'image/jpeg',
      quality
    );
  });
}

/**
 * Process an image for collection cover
 */
export async function processCollectionCover(file: File): Promise<ProcessedImage> {
  return processImage(file, IMAGE_SIZES.collectionCover, IMAGE_QUALITY.standard);
}

/**
 * Process an image for item display (full size)
 */
export async function processItemImage(file: File): Promise<ProcessedImage> {
  return processImage(file, IMAGE_SIZES.itemFull, IMAGE_QUALITY.high);
}

/**
 * Process an image for custom field file uploads
 */
export async function processCustomFieldImage(file: File): Promise<ProcessedImage> {
  return processImage(file, IMAGE_SIZES.customField, IMAGE_QUALITY.standard);
}

/**
 * Generate a unique filename for storage
 */
export function generateImageFilename(userId: string, prefix: string): string {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 8);
  return `${userId}/${prefix}_${timestamp}_${random}.jpg`;
}

/**
 * Check if a string is a base64 data URL
 */
export function isBase64DataUrl(str: string): boolean {
  return str.startsWith('data:image/');
}

/**
 * Check if a string is a storage URL (https://)
 */
export function isStorageUrl(str: string): boolean {
  return str.startsWith('https://') || str.startsWith('http://');
}
